import { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/lib/supabaseClient';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check active sessions
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email, password, companyName, fullName = '') => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            company_name: companyName
          }
        }
      });
      if (error) {
        // Extract error message safely to avoid body stream issues
        let errorMessage = 'Failed to create account';
        if (error.status === 422) {
          errorMessage = 'Invalid email format or password too weak';
        } else if (error.status === 429) {
          errorMessage = 'Too many requests. Please try again later.';
        } else if (typeof error.message === 'string') {
          errorMessage = error.message;
        }
        return { data: null, error: { message: errorMessage } };
      }
      return { data, error: null };
    } catch (err) {
      console.error('SignUp error:', err);
      return { data: null, error: { message: 'Failed to create account. Please try again.' } };
    }
  };

  const signIn = async (email, password) => {
    try {
      const result = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      // Handle the result safely - don't re-read any response streams
      if (result.error) {
        const errorMessage = result.error?.message || 'Invalid email or password';
        return { data: null, error: { message: errorMessage } };
      }
      
      return { data: result.data, error: null };
    } catch (err) {
      // Catch any stream-related errors and provide a clean error message
      console.error('SignIn error:', err?.message || err);
      return { data: null, error: { message: 'Login failed. Please try again.' } };
    }
  };

  // Google OAuth Sign In
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const signInWithGoogle = async () => {
    try {
      const redirectTo = window.location.origin + '/dashboard';
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectTo,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        }
      });
      
      if (error) {
        console.error('Google OAuth error:', error);
        return { data: null, error: { message: error.message || 'Failed to sign in with Google' } };
      }
      
      return { data, error: null };
    } catch (err) {
      console.error('Google SignIn error:', err);
      return { data: null, error: { message: 'Failed to sign in with Google. Please try again.' } };
    }
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    return { error };
  };

  // Get user profile metadata from Supabase
  const getUserProfile = () => {
    if (!user) return null;
    
    return {
      id: user.id,
      email: user.email,
      name: user.user_metadata?.full_name || user.user_metadata?.name || user.email?.split('@')[0],
      picture: user.user_metadata?.avatar_url || user.user_metadata?.picture || null,
      provider: user.app_metadata?.provider || 'email'
    };
  };

  const value = {
    user,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    getUserProfile
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
